import { requiresDocumentNavigation } from './document-policy';

/**
 * URL utilities for the navigation engine.
 *
 * All URL classification, path extraction, and link-filter logic
 * lives here. No DOM state, no globals - every function takes its
 * inputs explicitly so it can be reasoned about and exercised in
 * isolation.
 */

import type { NavScope } from './types';

/**
 * A clicked/hovered anchor, normalised at the DOM boundary.
 *
 * `document.querySelector('a')` and `Element.closest('a')` match
 * both HTML and SVG anchors - the type selector is namespace-
 * agnostic - but the two element types disagree on their
 * reflected properties: `HTMLAnchorElement.href` is the resolved
 * URL string, `SVGAElement.href` is an `SVGAnimatedString` that
 * stringifies to "[object SVGAnimatedString]". Reading `.href`
 * off an unchecked `HTMLAnchorElement` cast therefore rewrote
 * every SVG link to a 404 while `.target === '_blank'` never
 * matched. Resolving both once via `resolveNavLink()` and passing
 * plain strings around removes the type lie for every consumer.
 */
export interface NavLink {
  /** The anchor element itself (HTML or SVG). */
  readonly element: Element;
  /** Absolute resolved URL, as `HTMLAnchorElement.href` would give. */
  readonly href: string;
  /** The raw `target` attribute, or null when absent. */
  readonly target: string | null;
}

/**
 * Finds the nearest anchor ancestor of an event target and
 * resolves its URL and target the same way for HTML and SVG
 * anchors. Returns null when there is no anchor, or when its
 * href attribute is absent or unparseable - in both cases the
 * browser's own behaviour is the right one.
 */
export function resolveNavLink(target: EventTarget | null): NavLink | null {
  if (!(target instanceof Element)) return null;
  const element = target.closest('a');
  if (!element) return null;
  const rawHref = element.getAttribute('href');
  if (rawHref === null) return null;
  let href: string;
  try {
    href = new URL(rawHref, document.baseURI).href;
  }
  catch {
    return null;
  }
  return { element, href, target: element.getAttribute('target') };
}

/**
 * Extracts the pathname portion of a URL string, falling back to
 * the original string when parsing fails.
 */
export function extractPath(url: string): string {
  try {
    return new URL(url, location.origin).pathname;
  }
  catch {
    return url;
  }
}

/**
 * Classifies the navigation scope from the target URL.
 *
 * Binary classification: content-scope means the target path
 * matches the source path (pager, sort, filter, facet), full
 * scope means anything else.
 *
 * The source URL must be passed explicitly when the caller
 * cannot trust `location.href` to still point at the previously
 * committed page. The popstate handler is the canonical
 * offender: by the time the browser fires popstate, location
 * has already moved to the entry being navigated TO, so
 * comparing target to location would compare the URL to itself
 * and always classify as content-scope, telling the server "no
 * route change, return content only" when in reality the user
 * has crossed routes. Callers in that position pass the last
 * committed URL from the navigation state store; click-time
 * callers omit the argument and the default falls back to
 * `location.href`, which is correct for them because click
 * handlers run before any navigation has been committed.
 *
 * Shell-aware optimizations like "admin-to-admin stays in the
 * admin shell" are deliberately NOT implemented here because
 * they would require hardcoding path prefixes that vary between
 * sites and languages. The server decides whether to honor
 * shell-level caching based on route metadata.
 */
export function classifyScope(targetUrl: string, sourceUrl?: string): NavScope {
  try {
    const source = new URL(sourceUrl ?? location.href, location.origin);
    const target = new URL(targetUrl, location.origin);
    if (source.pathname === target.pathname) {
      return 'content';
    }
  }
  catch {
    // URL parse failure falls through to 'full'.
  }
  return 'full';
}

/**
 * Resolves the navigation scope for a clicked link.
 *
 * Explicit hints on the element override URL classification:
 * - `data-sdx-scope` attribute wins if set to a valid scope.
 * - Links inside a .pager or inside a table header cell are
 *   treated as content-scope because they represent pager/sort
 *   controls that mutate query parameters only.
 *
 * No hardcoded path prefixes - every branch works off DOM
 * structure or explicit attributes.
 */
export function getLinkScope(link: NavLink): NavScope {
  const explicit = link.element.getAttribute('data-sdx-scope');
  if (explicit === 'content' || explicit === 'full') {
    return explicit;
  }
  if (link.element.closest('.pager, [role="navigation"].pager')) {
    return 'content';
  }
  const cell = link.element.closest('th');
  if (cell && cell.closest('table')) {
    return 'content';
  }
  return classifyScope(link.href);
}

/**
 * Rewrites a URL so it is legal to write into history.
 *
 * The HTML spec forbids `history.pushState`/`replaceState` with a
 * URL whose username/password differ from the document's URL - on a
 * document opened through a credentialed URL
 * (`https://user:pass@host/`), pushing the credential-stripped form
 * throws a SecurityError. The navigation engine strips userinfo
 * from every URL before fetching (see `toFetchableUrl` in
 * `@sdx/core/transport`), so history writes must re-apply the
 * document's own userinfo to stay spec-legal. On documents without
 * embedded credentials (the overwhelmingly common case) the URL is
 * returned unchanged.
 */
export function toHistoryUrl(url: string): string {
  try {
    const doc = new URL(document.URL);
    if (doc.username === '' && doc.password === '') {
      return url;
    }
    const target = new URL(url, location.origin);
    target.username = doc.username;
    target.password = doc.password;
    return target.href;
  }
  catch {
    return url;
  }
}

export interface NavigableRules {
  /** Regex source applied to the target pathname to exclude paths. */
  excludePattern: string;
}

/**
 * Returns true when the engine should intercept a click on the
 * given anchor element.
 *
 * All bailouts are explicit: new tab, download link, opt-out data
 * attribute, core AJAX link, cross-origin, same-page hash nav,
 * and any path matched by the exclude pattern.
 */
export function isNavigable(link: NavLink | null, rules: NavigableRules): boolean {
  if (!link || !link.href) return false;
  if (link.target === '_blank') return false;
  if (link.element.hasAttribute('download')) return false;
  if (link.element.closest('[data-sdx-navigate="false"]')) return false;
  if (link.element.classList.contains('use-ajax')) return false;

  let url: URL;
  try {
    url = new URL(link.href, location.origin);
  }
  catch {
    return false;
  }

  if (url.origin !== location.origin) return false;
  if (requiresDocumentNavigation(url.href)) return false;
  if (url.pathname === location.pathname && url.hash) return false;

  try {
    if (new RegExp(rules.excludePattern).test(url.pathname)) return false;
  }
  catch {
    // Malformed exclude pattern means we cannot safely filter,
    // fall through to "navigable" rather than trapping every click.
  }
  return true;
}

/**
 * Returns true when the link is safe to prefetch on hover.
 *
 * CSRF token links are navigable (the click handler fetches them)
 * but NOT prefetchable — a prefetch would consume the one-shot
 * token before the user actually clicks.
 */
export function isPrefetchable(link: NavLink): boolean {
  try {
    return !new URL(link.href, location.origin).searchParams.has('token');
  }
  catch {
    return false;
  }
}
